import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getUserGameDataSummary, getUserGameData } from '../../api/userGameData';
import { spinEnergyAPI } from '../../api/spinEnergy';

// Async thunks
export const fetchGameDataSummary = createAsyncThunk(
    'gameData/fetchSummary',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getUserGameDataSummary();
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchEnergyStatus = createAsyncThunk(
    'gameData/fetchEnergy',
    async (_, { rejectWithValue }) => {
        try {
            const response = await spinEnergyAPI.getEnergyStatus();
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchFullGameData = createAsyncThunk(
    'gameData/fetchFull',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getUserGameData();
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

const initialState = {
    // Summary данные
    summary: {
        energy: 0,
        level: 0,
        experience: 0,
        coins: 0,
        gems: 0
    },
    
    // Энергия
    energy: {
        yang_energy: 0,
        yin_energy: 0,
        total_energy: 0,
        yang_percent: 0,
        yin_percent: 0,
        last_update: null,
        current_time: null,
        server_time: null,
        server_timezone: null,
        python_time: null,
        original_yang_energy: 0,
        original_yin_energy: 0,
        original_total_energy: 0
    },
    
    // Полные данные
    fullData: null,
    
    // Состояния загрузки
    loading: {
        summary: false,
        energy: false,
        fullData: false
    },
    
    // Ошибки
    errors: {
        summary: null,
        energy: null,
        fullData: null
    },
    
    // Метаданные
    lastUpdate: null,
    isUpdating: false
};

const gameDataSlice = createSlice({
    name: 'gameData',
    initialState,
    reducers: {
        // Обновление энергии после траты/добавления
        updateEnergy: (state, action) => {
            const { energyPoints, reason } = action.payload;
            if (reason === 'spend') {
                state.energy.yang_energy = Math.max(0, state.energy.yang_energy - energyPoints);
            } else if (reason === 'add') {
                state.energy.yang_energy += energyPoints;
            }
            state.energy.total_energy = state.energy.yang_energy + state.energy.yin_energy;
            state.lastUpdate = Date.now();
        },
        
        // Обновление после крутки
        updateAfterSpin: (state, action) => {
            const spinResult = action.payload;
            if (spinResult.energy_change) {
                state.energy.yang_energy += spinResult.energy_change;
                state.energy.total_energy = state.energy.yang_energy + state.energy.yin_energy;
            }
            if (spinResult.experience_gain) {
                state.summary.experience += spinResult.experience_gain;
            }
            state.lastUpdate = Date.now();
        },
        
        // Сброс ошибок
        clearErrors: (state) => {
            state.errors = {
                summary: null,
                energy: null,
                fullData: null
            };
        },
        
        // Установка состояния обновления
        setUpdating: (state, action) => {
            state.isUpdating = action.payload;
        }
    },
    extraReducers: (builder) => {
        // Summary
        builder
            .addCase(fetchGameDataSummary.pending, (state) => {
                state.loading.summary = true;
                state.errors.summary = null;
            })
            .addCase(fetchGameDataSummary.fulfilled, (state, action) => {
                state.loading.summary = false;
                state.summary = action.payload;
                state.lastUpdate = Date.now();
            })
            .addCase(fetchGameDataSummary.rejected, (state, action) => {
                state.loading.summary = false;
                state.errors.summary = action.payload;
            });
        
        // Energy
        builder
            .addCase(fetchEnergyStatus.pending, (state) => {
                state.loading.energy = true;
                state.errors.energy = null;
            })
            .addCase(fetchEnergyStatus.fulfilled, (state, action) => {
                state.loading.energy = false;
                state.energy = action.payload;
                state.lastUpdate = Date.now();
            })
            .addCase(fetchEnergyStatus.rejected, (state, action) => {
                state.loading.energy = false;
                state.errors.energy = action.payload;
            });
        
        // Full data
        builder
            .addCase(fetchFullGameData.pending, (state) => {
                state.loading.fullData = true;
                state.errors.fullData = null;
            })
            .addCase(fetchFullGameData.fulfilled, (state, action) => {
                state.loading.fullData = false;
                state.fullData = action.payload;
                state.lastUpdate = Date.now();
            })
            .addCase(fetchFullGameData.rejected, (state, action) => {
                state.loading.fullData = false;
                state.errors.fullData = action.payload;
            });
    }
});

export const { 
    updateEnergy, 
    updateAfterSpin, 
    clearErrors, 
    setUpdating 
} = gameDataSlice.actions;

// Selectors
export const selectGameDataSummary = (state) => state.gameData.summary;
export const selectEnergyStatus = (state) => state.gameData.energy;
export const selectFullGameData = (state) => state.gameData.fullData;
export const selectGameDataLoading = (state) => state.gameData.loading;
export const selectGameDataErrors = (state) => state.gameData.errors;
export const selectLastUpdate = (state) => state.gameData.lastUpdate;
export const selectIsUpdating = (state) => state.gameData.isUpdating;

export default gameDataSlice.reducer;
